// ProgressUtils.js - Utility functions for progress data handling


import React, { useEffect, useRef, useState } from 'react';
import { Calendar, TrendingUp, TrendingDown, Info, BarChart3, X, Minus } from 'lucide-react';

import './NewCSS.css';

// ProgressUtils.js - Fixed to only use physical_progress and financial_progress

// Get month name from month number
export const getMonthName = (month) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[parseInt(month) - 1] || month;
};

// Format month-year string
export const formatMonthYear = (month, year) => {
    return `${getMonthName(month)} ${year}`;
};

export const getLatestValidProgress = (progressArray) => {
    if (!progressArray || progressArray.length === 0) return null;

    const sortedProgress = [...progressArray].sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        return b.month - a.month;
    });

    for (const progress of sortedProgress) {
        const hasPhysical = progress.physical_progress !== null &&
            progress.physical_progress !== undefined &&
            progress.physical_progress !== '';
        const hasFinancial = progress.financial_progress !== null &&
            progress.financial_progress !== undefined &&
            progress.financial_progress !== '';

        if (hasPhysical || hasFinancial) {
            return {
                ...progress,
                monthName: getMonthName(progress.month),
                displayDate: `${getMonthName(progress.month)} ${progress.year}`,
                physical: progress.physical_progress ? parseFloat(progress.physical_progress) : null,
                financial: progress.financial_progress ? parseFloat(progress.financial_progress) : null
            };
        }
    }

    return null;
};

// Update getProgressHistory to return parsed numbers
export const getProgressHistory = (progressArray) => {
    if (!progressArray || progressArray.length === 0) return [];

    const validProgress = progressArray.filter(p => {
        const hasPhysical = p.physical_progress !== null &&
            p.physical_progress !== undefined &&
            p.physical_progress !== '';
        const hasFinancial = p.financial_progress !== null &&
            p.financial_progress !== undefined &&
            p.financial_progress !== '';
        return hasPhysical || hasFinancial;
    });

    return [...validProgress]
        .sort((a, b) => {
            if (a.year !== b.year) return a.year - b.year;
            return a.month - a.month;
        })
        .map(p => ({
            ...p,
            monthName: getMonthName(p.month),
            displayDate: `${getMonthName(p.month)} ${p.year}`,
            physical: p.physical_progress ? parseFloat(p.physical_progress) : null,
            financial: p.financial_progress ? parseFloat(p.financial_progress) : null
        }));
};

// Update getAllProgressForLineGraph to return parsed numbers
export const getAllProgressForLineGraph = (progressArray) => {
    if (!progressArray || progressArray.length === 0) return [];

    const sorted = [...progressArray].sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        return a.month - b.month;
    });

    return sorted.map(p => ({
        ...p,
        monthName: getMonthName(p.month),
        displayDate: `${getMonthName(p.month)} ${p.year}`,
        physical: (p.physical_progress !== null && p.physical_progress !== undefined && p.physical_progress !== '')
            ? parseFloat(p.physical_progress) : null,
        financial: (p.financial_progress !== null && p.financial_progress !== undefined && p.financial_progress !== '')
            ? parseFloat(p.financial_progress) : null
    }));
};

// ProgressBarGraph.jsx - Fixed component

// ProgressBarGraph.jsx - Fixed version with proper number parsing

const ProgressBarGraph = ({ contractor, contractorName }) => {
    const [showLineGraph, setShowLineGraph] = useState(false);
    const [hoveredBar, setHoveredBar] = useState(null);

    const progressArray = contractor.progress || [];
    const latestProgress = getLatestValidProgress(progressArray);
    const progressHistory = getProgressHistory(progressArray);
    const allProgressData = getAllProgressForLineGraph(progressArray);

    if (!latestProgress) {
        return (
            <div className="progress-no-data">
                <p>No progress data available</p>
            </div>
        );
    }

    // Parse values as floats to handle string numbers
    const physical = parseFloat(latestProgress.physical) || 0;
    const financial = parseFloat(latestProgress.financial) || 0;
    const displayDate = latestProgress.displayDate;

    const getProgressColor = (value) => {
        if (value >= 75) return '#10b981';
        if (value >= 50) return '#f59e0b';
        if (value >= 25) return '#f97316';
        return '#ef4444';
    };

    const physicalColor = getProgressColor(physical);
    const financialColor = getProgressColor(financial);

    return (
        <>
            <div className="progress-bar-graph-modern">
                <div className="progress-header-modern">
                    <div className="progress-title-section">
                        <h4>Project Progress</h4>
                        <div className="progress-date-badge">
                            <Calendar size={12} />
                            <span>Latest: {displayDate}</span>
                        </div>
                    </div>
                    {/* <button
                        className="view-line-graph-btn"
                        onClick={() => setShowLineGraph(true)}
                        disabled={allProgressData.length === 0}
                    >
                        <BarChart3 size={14} />
                        <span>View History</span>
                    </button> */}
                </div>

                <div className="progress-bars-container">
                    {/* Physical Progress Bar */}
                    <div className="progress-bar-item-modern">
                        <div className="progress-bar-label">
                            <div className="label-with-icon">
                                <span className="physical-dot"></span>
                                <span>Physical Progress</span>
                            </div>
                            <span className="progress-percentage">{physical.toFixed(1)}%</span>
                        </div>
                        <div className="progress-bar-wrapper">
                            <div
                                className="progress-bar-fill-modern physical-fill"
                                style={{ width: `${physical}%` }}
                                onMouseEnter={() => setHoveredBar('physical')}
                                onMouseLeave={() => setHoveredBar(null)}
                            >
                                {hoveredBar === 'physical' && physical > 15 && (
                                    <span className="progress-tooltip">{physical.toFixed(1)}%</span>
                                )}
                            </div>
                        </div>
                        {physical === 0 && <span className="progress-note">Not started</span>}
                    </div>

                    {/* Financial Progress Bar */}
                    <div className="progress-bar-item-modern">
                        <div className="progress-bar-label">
                            <div className="label-with-icon">
                                <span className="financial-dot"></span>
                                <span>Financial Progress</span>
                            </div>
                            <span className="progress-percentage">{financial.toFixed(1)}%</span>
                        </div>
                        <div className="progress-bar-wrapper">
                            <div
                                className="progress-bar-fill-modern financial-fill"
                                style={{ width: `${financial}%` }}
                                onMouseEnter={() => setHoveredBar('financial')}
                                onMouseLeave={() => setHoveredBar(null)}
                            >
                                {hoveredBar === 'financial' && financial > 15 && (
                                    <span className="progress-tooltip">{financial.toFixed(1)}%</span>
                                )}
                            </div>
                        </div>
                        {financial === 0 && <span className="progress-note">Not started</span>}
                    </div>
                </div>


            </div>

            {/* Line Graph Modal */}
            {showLineGraph && (
                <ProgressLineGraphModal
                    allProgressData={allProgressData}
                    progressHistory={progressHistory}
                    contractorName={contractorName}
                    onClose={() => setShowLineGraph(false)}
                />
            )}
        </>
    );
};

// ProgressLineGraphModal.jsx - Fixed version with proper number parsing

const ProgressLineGraphModal = ({ allProgressData, progressHistory, contractorName, onClose }) => {
    const canvasRef = useRef(null);
    const [hoveredPoint, setHoveredPoint] = useState(null);

    useEffect(() => {
        if (allProgressData.length > 0) {
            // Small delay to ensure canvas is ready
            setTimeout(() => drawLineGraph(), 100);
        }
    }, [allProgressData]);

    const drawLineGraph = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const width = canvas.width;
        const height = canvas.height;

        // Clear canvas
        ctx.clearRect(0, 0, width, height);

        // Set dimensions
        const padding = { top: 50, right: 60, bottom: 70, left: 60 };
        const graphWidth = width - padding.left - padding.right;
        const graphHeight = height - padding.top - padding.bottom;

        // Parse all progress data to numbers
        const parsedData = allProgressData.map(p => ({
            ...p,
            physical: p.physical !== null ? parseFloat(p.physical) : null,
            financial: p.financial !== null ? parseFloat(p.financial) : null
        }));

        // Get valid data points for scaling
        const validPoints = parsedData.filter(p => p.physical !== null || p.financial !== null);
        if (validPoints.length === 0) return;

        // Get max value for scaling (max of 100 or highest value)
        const maxValue = Math.max(100, ...validPoints.flatMap(p => [
            p.physical || 0,
            p.financial || 0
        ]));
        const yMax = Math.ceil(maxValue / 10) * 10;

        // Draw grid lines and Y-axis labels
        ctx.strokeStyle = '#e2e8f0';
        ctx.lineWidth = 0.5;
        ctx.fillStyle = '#64748b';
        ctx.font = '10px Inter';

        for (let i = 0; i <= 5; i++) {
            const y = padding.top + (i / 5) * graphHeight;
            const value = Math.round((5 - i) * (yMax / 5));

            ctx.beginPath();
            ctx.moveTo(padding.left, y);
            ctx.lineTo(width - padding.right, y);
            ctx.stroke();

            ctx.fillText(`${value}%`, 45, y + 3);
        }

        // Draw axes
        ctx.beginPath();
        ctx.moveTo(padding.left, padding.top);
        ctx.lineTo(padding.left, height - padding.bottom);
        ctx.lineTo(width - padding.right, height - padding.bottom);
        ctx.stroke();

        // Draw X-axis labels
        const xStep = graphWidth / (parsedData.length - 1 || 1);
        parsedData.forEach((point, idx) => {
            const x = padding.left + idx * xStep;

            ctx.fillStyle = '#64748b';
            ctx.font = '9px Inter';
            ctx.save();
            ctx.translate(x, height - padding.bottom + 5);
            ctx.rotate(-45);
            ctx.fillText(point.displayDate, 0, 0);
            ctx.restore();
        });

        // Draw physical progress line
        const physicalPoints = parsedData.map((p, idx) => ({
            x: padding.left + idx * xStep,
            y: p.physical !== null ? padding.top + graphHeight - (p.physical / yMax) * graphHeight : null,
            value: p.physical,
            date: p.displayDate,
            idx
        })).filter(p => p.y !== null && !isNaN(p.y));

        if (physicalPoints.length > 1) {
            ctx.beginPath();
            ctx.strokeStyle = '#10b981';
            ctx.lineWidth = 2.5;

            physicalPoints.forEach((point, idx) => {
                if (idx === 0) {
                    ctx.moveTo(point.x, point.y);
                } else {
                    ctx.lineTo(point.x, point.y);
                }
            });
            ctx.stroke();

            // Draw points for physical
            physicalPoints.forEach((point) => {
                ctx.beginPath();
                ctx.fillStyle = '#10b981';
                ctx.arc(point.x, point.y, 5, 0, 2 * Math.PI);
                ctx.fill();
                ctx.fillStyle = 'white';
                ctx.beginPath();
                ctx.arc(point.x, point.y, 2.5, 0, 2 * Math.PI);
                ctx.fill();
            });
        }

        // Draw financial progress line
        const financialPoints = parsedData.map((p, idx) => ({
            x: padding.left + idx * xStep,
            y: p.financial !== null ? padding.top + graphHeight - (p.financial / yMax) * graphHeight : null,
            value: p.financial,
            date: p.displayDate,
            idx
        })).filter(p => p.y !== null && !isNaN(p.y));

        if (financialPoints.length > 1) {
            ctx.beginPath();
            ctx.strokeStyle = '#f59e0b';
            ctx.lineWidth = 2.5;

            financialPoints.forEach((point, idx) => {
                if (idx === 0) {
                    ctx.moveTo(point.x, point.y);
                } else {
                    ctx.lineTo(point.x, point.y);
                }
            });
            ctx.stroke();

            // Draw points for financial
            financialPoints.forEach((point) => {
                ctx.beginPath();
                ctx.fillStyle = '#f59e0b';
                ctx.arc(point.x, point.y, 5, 0, 2 * Math.PI);
                ctx.fill();
                ctx.fillStyle = 'white';
                ctx.beginPath();
                ctx.arc(point.x, point.y, 2.5, 0, 2 * Math.PI);
                ctx.fill();
            });
        }

        // Title
        ctx.fillStyle = '#1e293b';
        ctx.font = 'bold 14px Inter';
        const title = `${contractorName || 'Contractor'} - Progress Trend Analysis`;
        const titleWidth = ctx.measureText(title).width;
        ctx.fillText(title, width / 2 - titleWidth / 2, 25);

        // Legend
        ctx.fillStyle = '#10b981';
        ctx.fillRect(width - 150, 15, 12, 12);
        ctx.fillStyle = '#1e293b';
        ctx.font = '11px Inter';
        ctx.fillText('Physical Progress', width - 135, 25);

        ctx.fillStyle = '#f59e0b';
        ctx.fillRect(width - 150, 35, 12, 12);
        ctx.fillStyle = '#1e293b';
        ctx.fillText('Financial Progress', width - 135, 45);
    };

    // Calculate trend statistics with proper number parsing
    const calculateTrend = () => {
        if (progressHistory.length < 2) return null;

        // Parse values to numbers
        const firstPhysical = progressHistory[0]?.physical !== null && progressHistory[0]?.physical !== undefined
            ? parseFloat(progressHistory[0].physical) : null;
        const lastPhysical = progressHistory[progressHistory.length - 1]?.physical !== null && progressHistory[progressHistory.length - 1]?.physical !== undefined
            ? parseFloat(progressHistory[progressHistory.length - 1].physical) : null;
        const firstFinancial = progressHistory[0]?.financial !== null && progressHistory[0]?.financial !== undefined
            ? parseFloat(progressHistory[0].financial) : null;
        const lastFinancial = progressHistory[progressHistory.length - 1]?.financial !== null && progressHistory[progressHistory.length - 1]?.financial !== undefined
            ? parseFloat(progressHistory[progressHistory.length - 1].financial) : null;

        const physicalChange = (lastPhysical !== null && firstPhysical !== null)
            ? lastPhysical - firstPhysical : null;
        const financialChange = (lastFinancial !== null && firstFinancial !== null)
            ? lastFinancial - firstFinancial : null;

        return { physicalChange, financialChange };
    };

    const trend = calculateTrend();

    // Find the latest entry with both metrics (parse values to numbers)
    const latestComplete = progressHistory.slice().reverse().find(p => {
        const physical = p.physical !== null && p.physical !== undefined ? parseFloat(p.physical) : null;
        const financial = p.financial !== null && p.financial !== undefined ? parseFloat(p.financial) : null;
        return physical !== null && financial !== null;
    });

    return (
        <div className="line-graph-modal-overlay" onClick={onClose}>
            <div className="line-graph-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <div className="modal-title">
                        <TrendingUp size={20} />
                        <h3>Progress Trend Analysis</h3>
                    </div>
                    <button className="close-modal-btn" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                {trend && (
                    <div className="trend-summary">
                        <div className="trend-card">
                            <span className="trend-label">Physical Progress Trend</span>
                            <div className={`trend-value ${trend.physicalChange > 0 ? 'positive' : trend.physicalChange < 0 ? 'negative' : 'neutral'}`}>
                                {trend.physicalChange !== null ? (
                                    <>
                                        {trend.physicalChange > 0 ? <TrendingUp size={16} /> : trend.physicalChange < 0 ? <TrendingDown size={16} /> : <Minus size={16} />}
                                        <span>{trend.physicalChange > 0 ? '+' : ''}{trend.physicalChange.toFixed(1)}%</span>
                                    </>
                                ) : <span>Insufficient data</span>}
                            </div>
                        </div>
                        <div className="trend-card">
                            <span className="trend-label">Financial Progress Trend</span>
                            <div className={`trend-value ${trend.financialChange > 0 ? 'positive' : trend.financialChange < 0 ? 'negative' : 'neutral'}`}>
                                {trend.financialChange !== null ? (
                                    <>
                                        {trend.financialChange > 0 ? <TrendingUp size={16} /> : trend.financialChange < 0 ? <TrendingDown size={16} /> : <Minus size={16} />}
                                        <span>{trend.financialChange > 0 ? '+' : ''}{trend.financialChange.toFixed(1)}%</span>
                                    </>
                                ) : <span>Insufficient data</span>}
                            </div>
                        </div>
                        {latestComplete && (
                            <div className="trend-card">
                                <span className="trend-label">Latest Complete Data</span>
                                <div className="trend-value neutral">
                                    <span>{latestComplete.displayDate}</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="graph-container">
                    <canvas
                        ref={canvasRef}
                        width={900}
                        height={450}
                        style={{ width: '100%', height: 'auto', background: 'white' }}
                    />
                </div>

                <div className="progress-data-table">
                    <h4>Historical Progress Data</h4>
                    <div className="data-table-wrapper">
                        <table className="progress-history-table">
                            <thead>
                                <tr>
                                    <th>Period</th>
                                    <th>Physical Progress</th>
                                    <th>Financial Progress</th>
                                    <th>Gap</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {progressHistory.map((progress, idx) => {
                                    // Parse values to numbers
                                    const physical = progress.physical !== null && progress.physical !== undefined
                                        ? parseFloat(progress.physical) : null;
                                    const financial = progress.financial !== null && progress.financial !== undefined
                                        ? parseFloat(progress.financial) : null;

                                    // Calculate gap
                                    const gapNum = (physical !== null && financial !== null)
                                        ? physical - financial : null;
                                    const gap = gapNum !== null ? gapNum.toFixed(1) : null;

                                    const gapStatus = gapNum !== null
                                        ? (gapNum > 0 ? 'Physical ahead' : gapNum < 0 ? 'Financial ahead' : 'Balanced')
                                        : 'Incomplete';

                                    return (
                                        <tr key={idx}>
                                            <td className="period-cell">{progress.displayDate}</td>
                                            <td className={physical !== null ? 'has-data' : 'no-data'}>
                                                {physical !== null ? `${physical.toFixed(1)}%` : '—'}
                                            </td>
                                            <td className={financial !== null ? 'has-data' : 'no-data'}>
                                                {financial !== null ? `${financial.toFixed(1)}%` : '—'}
                                            </td>
                                            <td className={gapNum !== null ? (gapNum > 0 ? 'positive-gap' : gapNum < 0 ? 'negative-gap' : '') : 'no-data'}>
                                                {gap !== null ? `${gapNum > 0 ? '+' : ''}${gap}%` : '—'}
                                            </td>
                                            <td className={gapNum !== null ? (gapNum > 0 ? 'positive-gap' : gapNum < 0 ? 'negative-gap' : '') : 'no-data'}>
                                                {gapStatus}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="modal-footer">
                    <div className="footer-info">
                        <small>⚠️ Note: Only showing records with valid physical_progress or financial_progress data</small>
                    </div>
                    <button className="close-btn" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
};

export { ProgressBarGraph }